import { computed, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SHM_VARIABLES } from '@cosmos/shared';
import {
  DatapointAdapter, DatapointView, DatapointValue, CompareEntry, DatapointAttachment, pctDelta,
} from '../datapoint.model';

export interface ShmAdapterDeps {
  measurementId: string;
  channelKey: string;
  channelData: Record<string, any[]>;   // channelData[channelKey] — parallel arrays incl. time, csvRef
  channelType: 'corrosion' | 'ph' | 'chlorides';
  startIndex: number;
  highlightKey?: string;
  canDelete: boolean;
  csvLookup?: (ingestedCsvId: string) => { filename?: string; status?: string } | undefined;
  onDelete?: (ctx: { channelKey: string; ts: Date }) => Observable<unknown>;
}

/** Non-charted / non-numeric keys we still surface as-is in the values list. */
const PASSTHROUGH_KEYS = ['method', 'saturated', 'saturationState', 'scale'];
/** Keys that are internal plumbing, never shown. */
const HIDDEN_KEYS = ['time', 'time_ecorr', 'csvRef'];

export class SHMDatapointAdapter implements DatapointAdapter {
  private readonly cursor = signal(this.deps.startIndex);
  readonly current: Signal<DatapointView> = computed(() => this.buildView(this.cursor()));
  readonly canDelete = computed(() => this.deps.canDelete);
  readonly affectedCount = computed(() => {
    // charge/thickLoss are cumulative from the removed index onward
    const total = this.length();
    return Math.max(0, total - 1 - this.cursor());
  });

  constructor(private readonly deps: ShmAdapterDeps) {}

  next(): void {
    const total = this.length();
    this.cursor.update((i) => Math.min(i + 1, total - 1));
  }

  prev(): void {
    this.cursor.update((i) => Math.max(i - 1, 0));
  }

  delete(): Observable<unknown> {
    if (!this.deps.onDelete) return of(null);
    const ts = new Date(this.deps.channelData.time[this.cursor()]);
    return this.deps.onDelete({ channelKey: this.deps.channelKey, ts });
  }

  // ── internals ────────────────────────────────────────────────────────────

  private length(): number {
    return (this.deps.channelData.time ?? []).length;
  }

  private varMeta(key: string): { label: string; unit?: string } {
    const group = (SHM_VARIABLES as any)[this.deps.channelType] ?? {};
    const meta = group[key];
    return meta ? { label: meta.label, unit: meta.unit } : { label: key };
  }

  private rowValues(i: number): DatapointValue[] {
    const cd = this.deps.channelData;
    const out: DatapointValue[] = [];
    for (const key of Object.keys(cd)) {
      if (HIDDEN_KEYS.includes(key)) continue;
      const raw = cd[key]?.[i];
      if (PASSTHROUGH_KEYS.includes(key)) {
        out.push({ key, label: key, value: raw ?? null });
        continue;
      }
      const { label, unit } = this.varMeta(key);
      out.push({ key, label, unit, value: typeof raw === 'number' ? raw : (raw ?? null) });
    }
    return out;
  }

  private compareAgainst(i: number, j: number): CompareEntry[] {
    const cd = this.deps.channelData;
    const entries: CompareEntry[] = [];
    for (const key of Object.keys(cd)) {
      if (HIDDEN_KEYS.includes(key) || PASSTHROUGH_KEYS.includes(key)) continue;
      const a = cd[key]?.[i];
      const b = cd[key]?.[j];
      if (typeof a !== 'number' || typeof b !== 'number') continue;
      const { label } = this.varMeta(key);
      entries.push({ key, label, delta: a - b, pct: pctDelta(b, a) });
    }
    return entries;
  }

  private attachments(i: number): DatapointAttachment[] {
    const id = this.deps.channelData.csvRef?.[i];
    if (!id) return [];
    const extra = this.deps.csvLookup?.(id) ?? {};
    return [{ type: 'csv', ingestedCsvId: String(id), filename: extra.filename, status: extra.status }];
  }

  private buildView(i: number): DatapointView {
    const total = this.length();
    const view: DatapointView = {
      sourceLabel: this.deps.channelKey,      // template maps to "Channel N" via translate
      timestamp: new Date(this.deps.channelData.time[i]),
      values: this.rowValues(i),
      highlightKey: this.deps.highlightKey,
      nav: { index: i, total, hasPrev: i > 0, hasNext: i < total - 1 },
      compare: {},
      attachments: this.attachments(i),
    };
    if (i > 0) view.compare.prev = this.compareAgainst(i, i - 1);
    if (i < total - 1) view.compare.next = this.compareAgainst(i, i + 1);
    return view;
  }
}
